import { MenuController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { Observable, filter, map, takeUntil } from 'rxjs';
import { Authentication, CartTotals, MenuItem, TipDetail, WorkDay } from '@core/models';
import { CART_FIND_TOTALS, CART_REMOVE, CART_UPDATE_STATE } from '@store/actions';
import {
	selectAuth,
	selectCartTotals,
	selectLanguaje,
	selectTipDetail,
	selectWorkDay,
} from '@store/selectors';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { CartService, I18nService, StoreService } from '@core/services';
import { selectOrderType } from '@store/selectors';
import { ComponentEnum } from '@core/enums';
import { IAbstractPresenter, ILanguageObject } from '@core/interfaces';
import { IShoppingCartComponent, IShoppingCartPresenter } from './interfaces';
import { ICartItemChange } from '@shared/components/cart-items/interfaces';
import { toObservable } from '@angular/core/rxjs-interop';
import { CartProcessorService } from './domain/cart-processor.service';

@Injectable({ providedIn: 'root' })
export class ShoppingcartPresenter
	extends IAbstractPresenter<IShoppingCartComponent>
	implements IShoppingCartPresenter
{
	emptyCartImg: string = 'assets/images/emptyCart.svg';
	startOverImg: string = 'assets/images/startOver.svg';
	cardImg: string = 'assets/images/card.svg';
	showSummary: boolean = false;
	orderType$: Observable<string | null>;
	workDay$: Observable<WorkDay | null>;
	auth$: Observable<Authentication | null>;

	tipDetail$: Observable<TipDetail | null>;
	cartItems$: Observable<MenuItem[]>;
	cartTotals$!: Observable<CartTotals>;
	translations: ILanguageObject;
	cartItemEmptySignal: WritableSignal<boolean> = signal<boolean>(true);
	cartItemEmpty$ = toObservable(this.cartItemEmptySignal);
	shoppingCartHeaderSignal: WritableSignal<string | null> = signal<string | null>(null);
	shoppingCartHeader$ = toObservable(this.shoppingCartHeaderSignal);

	currentStep: number = 1;

	constructor(
		private store: Store,
		private cartService: CartService,
		private storeService: StoreService,
		private menuCtrl: MenuController,
		private i18nService: I18nService,
		private cartProcessor: CartProcessorService
	) {
		super();

		this.cartItems$ = this.cartService.getStoreForStore().pipe(map(items => this.cartProcessor.calculateCartsWithSubtotalPrice(items).menuItems));
		this.cartTotals$ = this.store.select(selectCartTotals);
		this.orderType$ = this.store.select(selectOrderType);
		this.tipDetail$ = this.store.select(selectTipDetail);
		this.workDay$ = this.store.select(selectWorkDay);
		this.auth$ = this.store.select(selectAuth);
		this.translations = this.i18nService.getTranslations(ComponentEnum.ShoppingCart);
	}

	suscribeObservers(): void {
		this.store
			.select(selectLanguaje)
			.pipe(takeUntil(this.destroy$))
			.subscribe(lang => {
				this.view.translations = this.i18nService.getTranslations(ComponentEnum.ShoppingCart, lang);
			});

		this.cartItems$
			.pipe(
				map(items => this.cartProcessor.isCartEmpty(items)),
				takeUntil(this.destroy$)
			)
			.subscribe((cartItemEmpty: boolean) => {
				if (!cartItemEmpty) this.store.dispatch(CART_FIND_TOTALS());

				this.cartItemEmptySignal.set(cartItemEmpty);
			});

		this.tipDetail$
			.pipe(
				takeUntil(this.destroy$),
				filter(tipDetail => !!tipDetail)
			)
			.subscribe(() => {
				this.store.dispatch(CART_FIND_TOTALS());
			});

		this.cartItems$
			.pipe(
				map(cartItems => this.cartProcessor.getTotalQuantity(cartItems)),
				takeUntil(this.destroy$)
			)
			.subscribe((totalQuantity: number) => {
				let shoppingCartHeader = this.translations['yourCart'];

				if (totalQuantity) shoppingCartHeader = `${this.translations['yourCart']} (${totalQuantity})`;

				this.shoppingCartHeaderSignal.set(shoppingCartHeader);
			});
	}

	dispatchActions(): void {
		this.store.dispatch(CART_FIND_TOTALS());
	}

	onItemDeleted({ item: { idCartItem } }: { item: MenuItem; index: number }): void {
		this.store.dispatch(CART_REMOVE({ idArticulo: idCartItem }));

		if (this.cartItemEmptySignal()) this.closeCart();
	}

	closeCart(): void {
		this.menuCtrl.close('cartMenu');
	}

	changeAmount({ item, value }: ICartItemChange): void {
		const updatedItem = this.cartProcessor.processQuantityChange(item, value);
		this.store.dispatch(CART_UPDATE_STATE({ item: updatedItem }));
	}
}
